/**
 * Servicio de almacenamiento persistente
 * Maneja localStorage y sessionStorage de forma centralizada
 */

import config from '@/config';
import { Token } from '@/types';

// Claves de almacenamiento
const CUSTOM_TOKENS_KEY = 'custom_tokens';
const TOKEN_VISIBILITY_KEY = 'token_visibility';
const WALLETS_STORAGE_KEY = 'wallets_encrypted_data';
const WALLETS_INFO_KEY = 'wallets_info';
// ✅ UNIFICADO: Una sola clave para el índice seleccionado
const WALLET_INDEX_KEY = 'selected_wallet_index';

// Tipos
export interface WalletBasicInfo {
  address: string;
  name?: string;
  selectedNetwork?: string;
}

interface MultiWalletData {
  wallets: Record<string, any>; // address -> encrypted data
  walletsBasicInfo: WalletBasicInfo[];
  selectedIndex: number;
}

interface TokenVisibility {
  [network: string]: {
    [address: string]: boolean;
  };
}

/**
 * Servicio de almacenamiento
 */
export const storageService = {
  // ==================== RED ====================

  saveSelectedNetwork(networkId: string): void {
    if (!networkId) return;

    localStorage.setItem(config.storageKeys.selectedNetwork, networkId);

    // Notificar a los listeners (receive.service, etc.) que cambió la red
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('network:changed', { detail: { networkId } }));
    }
  },

  getSelectedNetwork(): string {
    const network = localStorage.getItem(config.storageKeys.selectedNetwork);
    return network || 'omdb';
  },

  // ==================== WALLETS ====================

  saveMultipleWallets(
    wallets: WalletBasicInfo[],
    encryptedData: any,
    selectedIndex: number,
    preserveSelection = false
  ): boolean {
    try {
      if (!wallets || !Array.isArray(wallets) || wallets.length === 0) {
        console.error('[Storage] No hay wallets para guardar');
        return false;
      }

      // Obtener índice actual si queremos preservar la selección
      let indexToUse = selectedIndex;
      if (preserveSelection) {
        const currentIndex = this.getSelectedWalletIndex();
        if (!isNaN(currentIndex) && currentIndex >= 0 && currentIndex < wallets.length) {
          indexToUse = currentIndex;
        }
      }

      // Validar índice
      if (indexToUse < 0 || indexToUse >= wallets.length) {
        indexToUse = 0;
      }

      // Obtener datos existentes o inicializar
      let walletsData = this.getMultipleWalletsData() || {
        wallets: {},
        walletsBasicInfo: [],
        selectedIndex: indexToUse,
      };

      // Actualizar datos encriptados si se proporcionan
      if (encryptedData) {
        const targetWallet = wallets[indexToUse];
        if (targetWallet?.address) {
          walletsData.wallets[targetWallet.address] = encryptedData;
          console.log(`[Storage] Actualizada wallet encriptada: ${targetWallet.address}`);
        }
      }

      // Actualizar información básica
      walletsData.walletsBasicInfo = wallets;
      walletsData.selectedIndex = indexToUse;

      // Guardar en localStorage
      localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
      this.saveSelectedWalletIndex(indexToUse);
      localStorage.setItem(WALLETS_INFO_KEY, JSON.stringify(wallets));

      return true;
    } catch (error) {
      console.error('[Storage] Error guardando múltiples wallets:', error);
      return false;
    }
  },

  getMultipleWalletsData(): MultiWalletData | null {
    try {
      const data = localStorage.getItem(WALLETS_STORAGE_KEY);
      if (!data) {
        return null;
      }

      const walletsData = JSON.parse(data);
      return walletsData;
    } catch (error) {
      console.error('[Storage] Error obteniendo datos de múltiples wallets:', error);
      return null;
    }
  },

  getWalletEncryptedData(address: string): any | null {
    try {
      const walletsData = this.getMultipleWalletsData();
      if (!walletsData?.wallets || !address) {
        return null;
      }

      const walletData = walletsData.wallets[address];
      if (!walletData) {
        console.log(`[Storage] No se encontraron datos para wallet ${address}`);
        return null;
      }

      console.log(`[Storage] Obtenidos datos encriptados para ${address}`);
      return walletData;
    } catch (error) {
      console.error(`[Storage] Error obteniendo datos de wallet ${address}:`, error);
      return null;
    }
  },

  saveWalletData(
    walletData: any,
    allWallets: WalletBasicInfo[],
    selectedIndex: number,
    preserveSelection = false
  ): void {
    if (!walletData) return;

    try {
      // Determinar índice a usar
      let indexToUse = selectedIndex;
      if (preserveSelection) {
        const currentIndex = this.getSelectedWalletIndex(); // ✅ Usar método unificado
        if (!isNaN(currentIndex)) {
          indexToUse = currentIndex;
        }
      }

      // Validar índice
      if (allWallets?.length > 0) {
        if (indexToUse < 0 || indexToUse >= allWallets.length) {
          indexToUse = 0;
        }
      }

      console.log(`[Storage] Guardando wallet - Dirección: ${walletData.address}`);

      // Guardar en clave tradicional (compatibilidad)
      localStorage.setItem(config.storageKeys.encryptedWallet, JSON.stringify(walletData));
      localStorage.setItem(config.storageKeys.hasWallet, 'true');

      // Guardar en sistema multiwallet
      if (allWallets?.length > 0) {
        const currentData = this.getMultipleWalletsData();
        let walletsData = currentData || {
          wallets: {},
          walletsBasicInfo: allWallets,
          selectedIndex: indexToUse,
        };

        walletsData.walletsBasicInfo = allWallets;

        if (walletData.address) {
          walletsData.wallets[walletData.address] = walletData;
        }

        walletsData.selectedIndex = indexToUse;

        localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
        this.saveSelectedWalletIndex(indexToUse);
        localStorage.setItem(WALLETS_INFO_KEY, JSON.stringify(allWallets));
      }

      // Guardar red seleccionada
      if (walletData.selectedNetwork) {
        this.saveSelectedNetwork(walletData.selectedNetwork);
      }
    } catch (error) {
      console.error('[Storage] Error guardando datos de wallet:', error);
    }
  },

  loadWalletData(specificAddress?: string): any | null {
    try {
      const walletsData = this.getMultipleWalletsData();

      // Si hay datos de múltiples wallets
      if (walletsData && walletsData.walletsBasicInfo && walletsData.walletsBasicInfo.length > 0) {
        let addressToLoad: string | null = null;

        // 1. Si hay dirección específica, usar esa
        if (specificAddress) {
          addressToLoad = specificAddress;
        }
        // 2. Usar índice unificado
        else {
          const index = this.getSelectedWalletIndex();
          if (index >= 0 && index < walletsData.walletsBasicInfo.length) {
            addressToLoad = walletsData.walletsBasicInfo[index].address;
          }
        }
        // 3. Usar primera wallet como fallback
        if (!addressToLoad && walletsData.walletsBasicInfo.length > 0) {
          addressToLoad = walletsData.walletsBasicInfo[0].address;
        }

        // Cargar datos de la wallet
        if (addressToLoad && walletsData.wallets[addressToLoad]) {
          const walletData = walletsData.wallets[addressToLoad];

          // Forzar red correcta
          const savedNetwork = this.getSelectedNetwork();
          if (savedNetwork && walletData.selectedNetwork !== savedNetwork) {
            walletData.selectedNetwork = savedNetwork;
          }

          console.log(`[Storage] Datos cargados - Dirección: ${addressToLoad}`);
          return walletData;
        }
      }

      // Fallback: carga tradicional
      const walletData = localStorage.getItem(config.storageKeys.encryptedWallet);
      if (!walletData) {
        return null;
      }

      const parsedData = JSON.parse(walletData);

      // Verificar dirección específica
      if (specificAddress && parsedData.address !== specificAddress) {
        return null;
      }

      // Forzar red correcta
      const savedNetwork = this.getSelectedNetwork();
      if (savedNetwork && parsedData.selectedNetwork !== savedNetwork) {
        parsedData.selectedNetwork = savedNetwork;
      }

      return parsedData;
    } catch (error) {
      console.error('[Storage] Error cargando datos de wallet:', error);
      return null;
    }
  },

  clearWalletData(keepMultipleWallets = false, specificAddress: string | null = null): void {
    if (!keepMultipleWallets) {
      localStorage.removeItem(config.storageKeys.encryptedWallet);
      localStorage.removeItem(config.storageKeys.hasWallet);
      localStorage.removeItem(WALLETS_STORAGE_KEY);
      localStorage.removeItem(WALLETS_INFO_KEY);
      localStorage.removeItem(WALLET_INDEX_KEY); // ✅ Usar clave unificada
      sessionStorage.clear();
      console.log('[Storage] Todos los datos eliminados');
      return;
    }

    // Eliminar wallet específica
    if (specificAddress) {
      try {
        const walletsData = this.getMultipleWalletsData();
        if (!walletsData || !walletsData.wallets[specificAddress]) {
          return;
        }

        // Antes de filtrar: recordar qué dirección estaba seleccionada, para
        // volver a encontrarla después (el índice puede correrse si la wallet
        // eliminada estaba antes de la seleccionada en el arreglo).
        const previouslySelectedAddress = walletsData.walletsBasicInfo[walletsData.selectedIndex]?.address;

        delete walletsData.wallets[specificAddress];

        walletsData.walletsBasicInfo = walletsData.walletsBasicInfo.filter(
          (w) => w.address !== specificAddress
        );

        if (walletsData.walletsBasicInfo.length > 0) {
          if (previouslySelectedAddress && previouslySelectedAddress !== specificAddress) {
            const newIndex = walletsData.walletsBasicInfo.findIndex((w) => w.address === previouslySelectedAddress);
            walletsData.selectedIndex = newIndex >= 0 ? newIndex : 0;
          } else {
            walletsData.selectedIndex = 0;
          }

          localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
          this.saveSelectedWalletIndex(walletsData.selectedIndex);
          localStorage.setItem(WALLETS_INFO_KEY, JSON.stringify(walletsData.walletsBasicInfo));

          console.log(`[Storage] Wallet ${specificAddress} eliminada`);
        } else {
          this.clearWalletData(false);
        }
      } catch (error) {
        console.error('[Storage] Error eliminando wallet específica:', error);
      }
    }
  },

  /**
   * Actualiza el blob encriptado de varias wallets en una sola escritura.
   */
  updateWalletsEncryptedData(updates: Record<string, any>): boolean {
    try {
      const walletsData = this.getMultipleWalletsData();
      if (!walletsData) return false;

      for (const [address, data] of Object.entries(updates)) {
        walletsData.wallets[address] = data;
      }

      localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
      return true;
    } catch (error) {
      console.error('[Storage] Error actualizando datos encriptados de wallets:', error);
      return false;
    }
  },

  renameWallet(address: string, name: string): boolean {
    try {
      const walletsData = this.getMultipleWalletsData();
      if (!walletsData) return false;

      const wallet = walletsData.walletsBasicInfo.find(
        (w) => w.address.toLowerCase() === address.toLowerCase()
      );
      if (!wallet) return false;

      wallet.name = name;
      localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
      localStorage.setItem(WALLETS_INFO_KEY, JSON.stringify(walletsData.walletsBasicInfo));
      return true;
    } catch (error) {
      console.error('[Storage] Error renombrando wallet:', error);
      return false;
    }
  },

  // ==================== ÍNDICE DE WALLET (FUENTE ÚNICA) ====================

  saveSelectedWalletIndex(index: number): boolean {
    try {
      if (index === undefined || index === null || isNaN(index)) {
        console.error('[Storage] Índice inválido');
        return false;
      }

      console.log(`[Storage] Guardando índice: ${index}`);

      // ✅ UNIFICADO: Solo una fuente de verdad
      localStorage.setItem(WALLET_INDEX_KEY, index.toString());

      // Actualizar también en sistema multiwallet para consistencia
      const walletsData = this.getMultipleWalletsData();
      if (walletsData) {
        walletsData.selectedIndex = index;
        localStorage.setItem(WALLETS_STORAGE_KEY, JSON.stringify(walletsData));
      }

      return true;
    } catch (error) {
      console.error('[Storage] Error guardando índice:', error);
      return false;
    }
  },

  getSelectedWalletIndex(): number {
    try {
      // ✅ UNIFICADO: Solo una fuente de verdad
      const index = localStorage.getItem(WALLET_INDEX_KEY);
      
      if (index && !isNaN(parseInt(index))) {
        return parseInt(index);
      }

      // Fallback: obtener desde sistema multiwallet
      const walletsData = this.getMultipleWalletsData();
      if (walletsData?.selectedIndex !== undefined) {
        // Sincronizar con la fuente principal
        this.saveSelectedWalletIndex(walletsData.selectedIndex);
        return walletsData.selectedIndex;
      }

      return 0;
    } catch (error) {
      console.error('[Storage] Error obteniendo índice:', error);
      return 0;
    }
  },

  // ==================== SESIÓN ====================

  saveSession(sessionData: any): void {
    if (!sessionData) return;

    try {
      sessionStorage.setItem(config.storageKeys.session, JSON.stringify(sessionData));
    } catch (error) {
      console.error('[Storage] Error guardando sesión:', error);
    }
  },

  getSession(): any | null {
    try {
      const sessionData = sessionStorage.getItem(config.storageKeys.session);
      if (!sessionData) return null;

      return JSON.parse(sessionData);
    } catch (error) {
      console.error('[Storage] Error obteniendo sesión:', error);
      return null;
    }
  },

  // ==================== TOKENS PERSONALIZADOS ====================

  saveCustomToken(token: Token): boolean {
    if (!token?.address || !token?.network) {
      console.error('[Storage] Token inválido');
      return false;
    }

    try {
      const existingTokens = this.getCustomTokens();

      // Verificar duplicados
      const exists = existingTokens.some(
        (t) =>
          t.address?.toLowerCase() === token.address?.toLowerCase() && t.network === token.network
      );

      if (exists) {
        console.log('[Storage] Token ya existe');
        return false;
      }

      existingTokens.push(token);
      localStorage.setItem(CUSTOM_TOKENS_KEY, JSON.stringify(existingTokens));
      this.setTokenVisibility(token.address, token.network, true);

      console.log(`[Storage] Token guardado: ${token.symbol}`);
      return true;
    } catch (error) {
      console.error('[Storage] Error guardando token:', error);
      return false;
    }
  },

  getCustomTokens(): Token[] {
    try {
      const tokensData = localStorage.getItem(CUSTOM_TOKENS_KEY);
      if (!tokensData) return [];

      const tokens = JSON.parse(tokensData);
      return Array.isArray(tokens) ? tokens : [];
    } catch (error) {
      console.error('[Storage] Error cargando tokens:', error);
      return [];
    }
  },

  removeCustomToken(address: string, network: string): boolean {
    if (!address || !network) return false;

    try {
      const tokens = this.getCustomTokens();
      const filteredTokens = tokens.filter(
        (token) =>
          !(token.address?.toLowerCase() === address.toLowerCase() && token.network === network)
      );

      if (filteredTokens.length === tokens.length) {
        return false;
      }

      localStorage.setItem(CUSTOM_TOKENS_KEY, JSON.stringify(filteredTokens));
      console.log(`[Storage] Token eliminado: ${address}`);
      return true;
    } catch (error) {
      console.error('[Storage] Error eliminando token:', error);
      return false;
    }
  },

  // ==================== VISIBILIDAD DE TOKENS ====================

  saveTokenVisibility(visibility: TokenVisibility): boolean {
    if (!visibility) return false;

    try {
      localStorage.setItem(TOKEN_VISIBILITY_KEY, JSON.stringify(visibility));
      console.log('[Storage] Visibilidad de tokens guardada');
      return true;
    } catch (error) {
      console.error('[Storage] Error guardando visibilidad:', error);
      return false;
    }
  },

  getTokenVisibility(): TokenVisibility {
    try {
      const visibilityData = localStorage.getItem(TOKEN_VISIBILITY_KEY);
      if (!visibilityData) return {};

      return JSON.parse(visibilityData);
    } catch (error) {
      console.error('[Storage] Error cargando visibilidad:', error);
      return {};
    }
  },

  setTokenVisibility(address: string, network: string, isVisible: boolean): boolean {
    if (!address || !network) return false;

    try {
      const visibility = this.getTokenVisibility();

      if (!visibility[network]) {
        visibility[network] = {};
      }

      visibility[network][address.toLowerCase()] = isVisible;
      this.saveTokenVisibility(visibility);

      console.log(`[Storage] Visibilidad de ${address} establecida a ${isVisible}`);
      return true;
    } catch (error) {
      console.error('[Storage] Error estableciendo visibilidad:', error);
      return false;
    }
  },

  isTokenVisible(address: string, network: string): boolean {
    if (!address || !network) return true;

    try {
      const normalizedAddress = address.toLowerCase();
      const visibility = this.getTokenVisibility();

      if (!visibility[network]) {
        return true;
      }

      const isVisible = visibility[network][normalizedAddress];
      return isVisible === undefined ? true : isVisible;
    } catch (error) {
      console.error('[Storage] Error obteniendo visibilidad:', error);
      return true;
    }
  },

  // ==================== GENÉRICOS ====================

  set(key: string, value: any): void {
    try {
      if (typeof value === 'object') {
        localStorage.setItem(key, JSON.stringify(value));
      } else {
        localStorage.setItem(key, String(value));
      }
    } catch (error) {
      console.error(`[Storage] Error guardando ${key}:`, error);
    }
  },

  get(key: string): string | null {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  },
};

// Definir tipo para exportación y mejor soporte de IDE
export type StorageServiceType = typeof storageService;

export default storageService;
